import {
  APPLIANCE_SHIPPING_COST,
  APPLIANCE_SHIPPING_MIN_MET,
  APPLIANCE_SHIPPING_MIN_NOT_MET,
  BODFS_CAR_SCHEDULE_DELIV,
  SCHEDULE_DELIV,
  SCHEDULE_DELIV_LATER,
  BODFS_CAR_SAME_DAY_DELIV,
  ODD_FASTER_DELIVERY,
  SAME_DAY,
  ON_DEMAND
} from '../constants';

const capitalizeFirstLetter = (word) => {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

const getOnDemandDeliveryText = (priorityDeliveryOptions) => {
  if (!priorityDeliveryOptions || priorityDeliveryOptions.length === 0) {
    return null;
  }
  const hasOnDemand = priorityDeliveryOptions.find((option) => option.type === ON_DEMAND);
  const hasSameDay = priorityDeliveryOptions.find((option) => option.type === SAME_DAY);

  // Will render 'Faster Delivery: Within 3 Hours (+$6.99). Schedule in Checkout.'
  // or Will render 'Faster Delivery: Within 3 Hours (+$4). Schedule in Checkout.'
  if (hasOnDemand && !hasSameDay) {
    return {
      titleText: ODD_FASTER_DELIVERY.TITLE_TEXT + ODD_FASTER_DELIVERY.WITHIN_TEXT + hasOnDemand.timeline,
      priceText: `(+$${Number(hasOnDemand.totalCharge)?.toFixed(2)}). `,
      bodyText: ODD_FASTER_DELIVERY.BODY_TEXT,
      isTextWrapping: true
    };
  }

  // Will render 'Faster Delivery: Today ($2.99). Schedule in Checkout.'
  if (!hasOnDemand && hasSameDay) {
    return {
      titleText: ODD_FASTER_DELIVERY.TITLE_TEXT + capitalizeFirstLetter(hasSameDay.timeline),
      priceText: `($${Number(hasSameDay.totalCharge)?.toFixed(2)}). `,
      bodyText: ODD_FASTER_DELIVERY.BODY_TEXT,
      isTextWrapping: true
    };
  }

  // Will render 'Faster Delivery: Today ($2.99) or Within 3 Hours ($6.99). Schedule in Checkout.'
  if (hasOnDemand && hasSameDay) {
    return {
      titleText: ODD_FASTER_DELIVERY.TITLE_TEXT + capitalizeFirstLetter(hasSameDay.timeline),
      sameDayPriceText: `($${hasSameDay.totalCharge}) or `,
      titleTextPart2: ODD_FASTER_DELIVERY.WITHIN_TEXT + hasOnDemand.timeline,
      priceText: ` ($${Number(hasOnDemand.totalCharge)?.toFixed(2)}). `,
      bodyText: ODD_FASTER_DELIVERY.BODY_TEXT,
      isTextWrapping: true
    };
  }

  return null;
};

const getBodfsText = ({
  bodfsDeliveryTimeline,
  bodfsFulfillment,
  isCartTiles,
  isSameBODFSDeliveryPriceFS
}) => {
  // $2.99 BODFS message scenario on PIP
  if (bodfsFulfillment?.hasSameDayCarDelivery
    && bodfsFulfillment?.sameDayDeliveryCharge
  ) {
    return {
      titleText:
        BODFS_CAR_SAME_DAY_DELIV.TITLE_TEXT
          + bodfsFulfillment?.sameDayDeliveryCharge
          + '. ',
      bodyText: BODFS_CAR_SAME_DAY_DELIV.BODY_TEXT,
      isTextWrapping: true
    };
  }

  // $1.99 BODFS message scenario on PIP
  if (isSameBODFSDeliveryPriceFS && !isCartTiles) {
    return {
      titleText:
          BODFS_CAR_SCHEDULE_DELIV.TITLE_TEXT1
          + bodfsDeliveryTimeline
          + BODFS_CAR_SCHEDULE_DELIV.TITLE_TEXT2
          + bodfsFulfillment?.deliveryCharge
          + '. ',
      bodyText: BODFS_CAR_SCHEDULE_DELIV.BODY_TEXT,
      isTextWrapping: true
    };
  }

  // Item is eligible for BODFS and delivery timeline is today/tomorrow
  if (bodfsDeliveryTimeline) {
    if (bodfsDeliveryTimeline !== 'later') {
      return {
        titleText: SCHEDULE_DELIV.TITLE_TEXT + bodfsDeliveryTimeline + '.',
        bodyText: SCHEDULE_DELIV.BODY_TEXT,
        isTextWrapping: true
      };
    }
    return {
      titleText: SCHEDULE_DELIV_LATER.TITLE_TEXT,
      bodyText: SCHEDULE_DELIV_LATER.BODY_TEXT
    };
  }
  return null;
};

const getMajorApplianceText = ({
  applianceFlatChargeApplies,
  forceFreeShippingForExchange,
  isCartTiles,
  isMinimumFreeShipPrice
}) => {
  // FEATURE SWITCH OVERRIDE
  // APPLIANCE Non Exchage Customer with enableApplianceDeliveryCharge
  if (applianceFlatChargeApplies) {
    return {
      titleText: APPLIANCE_SHIPPING_COST.TITLE_TEXT,
      bodyText: APPLIANCE_SHIPPING_COST.BODY_TEXT,
      bodyTextAdditional: !isCartTiles ? APPLIANCE_SHIPPING_COST.BODY_TEXT_ADDITIONAL : '',
      isTextWrapping: true
    };
  }

  // FEATURE SWITCH OVERRIDE
  // APPLIANCE Exchange customer with enableFreeDeliveryForExchange
  if (forceFreeShippingForExchange) {
    return {
      titleText: APPLIANCE_SHIPPING_MIN_MET.TITLE_TEXT,
      bodyText: APPLIANCE_SHIPPING_MIN_MET.BODY_TEXT,
      isTextWrapping: true
    };
  }

  // APPLIANCE + Free shipping minimum met
  if (isMinimumFreeShipPrice) {
    return {
      titleText: APPLIANCE_SHIPPING_MIN_MET.TITLE_TEXT,
      isTextWrapping: true
    };
  }

  // APPLIANCE + Free shipping minimum not met
  return {
    freeText: APPLIANCE_SHIPPING_MIN_NOT_MET.FREE_TEXT,
    titleText: APPLIANCE_SHIPPING_MIN_NOT_MET.TITLE_TEXT,
    bodyText: APPLIANCE_SHIPPING_MIN_NOT_MET.BODY_TEXT,
    isTextWrapping: true
  };
};

export const getDeliveryMessageContent = ({
  applianceFlatChargeApplies,
  bodfsDeliveryTimeline,
  bodfsFulfillment,
  forceFreeShippingForExchange,
  isBodfsAvailable,
  isCartTiles,
  isMajorApplianceDeliveryType,
  isMinimumFreeShipPrice,
  isSameBODFSDeliveryPriceFS,
  enableSameDayDeliveryChargeFS,
  priorityDeliveryOptions
}) => {
  let deliveryMessageContent;

  if (bodfsFulfillment) {
    const onDemandMessage = getOnDemandDeliveryText(priorityDeliveryOptions);
    if (onDemandMessage) return onDemandMessage;
  }

  if (isBodfsAvailable) {
    deliveryMessageContent = getBodfsText({
      bodfsDeliveryTimeline,
      bodfsFulfillment,
      isCartTiles,
      isSameBODFSDeliveryPriceFS,
      enableSameDayDeliveryChargeFS
    });
  }

  if (isMajorApplianceDeliveryType) {
    deliveryMessageContent = getMajorApplianceText({
      applianceFlatChargeApplies,
      forceFreeShippingForExchange,
      isCartTiles,
      isMinimumFreeShipPrice
    });
  }
  return deliveryMessageContent || { titleText: '', bodyText: '' };
};

export const getDeliveryMessageVars = ({
  applianceDeliveryData,
  enableApplianceDeliveryCharge,
  enableFreeDeliveryForExchange,
  shippingData,
  isBodfsAvailable,
  isExchangeCustomer,
  isMajorAppliance,
  priceForFreeShip
}) => {
  let isApplianceDataLoading = false;
  let isMinimumFreeShipPrice = false;
  let noDeliveryAvailable = false;
  // Feature switch Overrides
  let applianceFlatChargeApplies = false;
  let forceFreeShippingForExchange = false;

  if (isMajorAppliance) {
    isApplianceDataLoading = !applianceDeliveryData;
    isMinimumFreeShipPrice = priceForFreeShip;
  } else {
    noDeliveryAvailable = !shippingData && !isBodfsAvailable;
  }

  if (isExchangeCustomer) {
    forceFreeShippingForExchange = enableFreeDeliveryForExchange;
  } else {
    applianceFlatChargeApplies = enableApplianceDeliveryCharge;
  }

  return {
    applianceFlatChargeApplies,
    forceFreeShippingForExchange,
    isApplianceDataLoading,
    isMinimumFreeShipPrice,
    noDeliveryAvailable
  };
};
